import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, googleProvider, db } from '../firebase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const createUserProfile = async (user, additionalData = {}) => {
    const userRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      const { displayName, email } = user;
      const createdAt = new Date();
      const today = new Date().toDateString();
      
      try {
        await setDoc(userRef, {
          displayName,
          email,
          createdAt,
          dailyRequests: 0,
          lastRequestDate: today,
          isAdmin: false,
          ...additionalData
        });
      } catch (error) {
        console.log('Error creating user profile', error.message);
      }
    }
    
    return getUserProfile(user.uid);
  };

  const getUserProfile = async (uid) => {
    const userRef = doc(db, 'users', uid);
    const userDoc = await getDoc(userRef);
    return userDoc.exists() ? userDoc.data() : null;
  };

  const updateDailyRequest = async () => {
    if (currentUser && userProfile) {
      const userRef = doc(db, 'users', currentUser.uid);
      const today = new Date().toDateString();
      
      // Reset daily count if it's a new day
      const newDailyRequests = userProfile.lastRequestDate === today 
        ? (userProfile.dailyRequests || 0) + 1 
        : 1;
      
      await setDoc(userRef, {
        ...userProfile,
        dailyRequests: newDailyRequests,
        lastRequestDate: today
      });
      
      setUserProfile(prev => ({
        ...prev,
        dailyRequests: newDailyRequests,
        lastRequestDate: today
      }));
    }
  };

  const login = async (email, password) => {
    const result = await signInWithEmailAndPassword(auth, email, password);
    await createUserProfile(result.user);
    return result;
  };

  const signup = async (email, password) => {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    await createUserProfile(result.user);
    return result;
  };

  const loginWithGoogle = async () => {
    const result = await signInWithPopup(auth, googleProvider);
    await createUserProfile(result.user);
    return result;
  };

  const logout = () => {
    setUserProfile(null);
    return signOut(auth);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        const profile = await getUserProfile(user.uid);
        setUserProfile(profile);
      } else {
        setUserProfile(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userProfile,
    login,
    signup,
    loginWithGoogle,
    logout,
    updateDailyRequest,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
